import { LaasSettingService } from 'src/app/core/services/laas-setting.service';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { Injector } from '@angular/core';
import { RouteStateService } from 'src/app/core/services/route-state.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { LoaderService } from 'src/app/core/services/loader.service';
import { ConfirmationService } from 'primeng/api';
import { CacheService } from 'src/app/core/services/cache.service';
import { LaasConstants } from 'src/app/core/constants/laas-constants.constant';
import { isNullOrEmpty, laaSFormatString, readBlobAsJson } from 'src/app/core/utils/common-functions';

export class BaseComponent {

  logger: NGXLogger;
  routeStateService: RouteStateService;
  toastService: ToastService;
  loader: LoaderService;
  translateService: TranslateService;
  laasSetting: LaasSettingService;
  confirmationService: ConfirmationService;
  cacheService: CacheService;

  constructor(injector: Injector) {
    this.logger = injector.get(NGXLogger);
    this.routeStateService = injector.get(RouteStateService);
    this.toastService = injector.get(ToastService);
    this.loader = injector.get(LoaderService);
    this.laasSetting = injector.get(LaasSettingService);
    this.translateService = injector.get(TranslateService);
    this.confirmationService = injector.get(ConfirmationService);
    this.cacheService = injector.get(CacheService);

    this.translateService.use(this.laasSetting.language);
  }

  toastErrorMessage(err: any): void {
    this.logger.error(err);
    if(isNullOrEmpty(err)) {
      throw new Error('No defined error.');
    }

    if(typeof err === 'string') {
      this.toastService.error(err);
      return;
    }

    // Multi-error branch: RFC 9457 / WarehousingValidationException response
    // Shape: { errors: [ { field, errorCode, errorMsg }, ... ] }
    if (Array.isArray(err.errors) && err.errors.length > 0) {
      const messages: string = err.errors
        .map((e: any) => {
          const msg = e.errorMsg || e.errorCode || '';
          return e.field ? `[${e.field}] ${msg}` : msg;
        })
        .filter((m: string) => m.length > 0)
        .join('\n');
      this.toastService.error(messages || err.title || 'Validation failed');
      return;
    }

    // Single-error branch (existing behaviour)
    const hasDatas = err.datas != null && err.datas.length > 0;
    var errMsg = this.resolveErrorMessageTemplate(err, hasDatas);

    // 兜底須早於 laaSFormatString：errMsg 為 undefined 時該函式會擲 TypeError，連 toast 都不會出現。
    // 對照表與回應皆無訊息時仍需說出「發生了什麼」，並附錯誤碼供追查；兜底訊息本身無佔位符，
    // 不套 datas 格式化。
    if (isNullOrEmpty(errMsg)) {
      const code = [err.category, err.errorCode].filter((part: any) => !isNullOrEmpty(part)).join('/');
      errMsg = this.translateService.instant('Common.Error.Unspecified')
        + (isNullOrEmpty(code) ? '' : ` (${code})`);
    } else if (hasDatas) {
      errMsg = laaSFormatString(errMsg, err.datas);
    }

    this.toastService.error(errMsg);
  }

  /**
   * 顯示以 Blob 回傳的錯誤內容。
   * 檔案下載以 responseType blob 呼叫，後端 ErrorCodeException 回的 JSON 錯誤內容也會是 Blob，須先讀成文字再解析
   * 才拿得到訊息；讀不到內容、內容不是 JSON 物件（閘道或代理層的純文字、HTML 錯誤頁），或回應與前端快取皆無訊息時，
   * 改顯示呼叫端指定的通用訊息，不讓使用者看到空白或無意義的提示。
   *
   * Shows an error body returned as a Blob. Downloads use responseType blob, so the backend's JSON error body
   * must be read as text and parsed before its message is reachable; unreadable, non-JSON or message-less
   * bodies fall back to the caller's generic message.
   *
   * @param errorBody       HttpErrorResponse.error，預期為 Blob (error body, expected to be a Blob)
   * @param fallbackMessage 無可用訊息時顯示的通用訊息 (generic message shown when no message is available)
   */
  async toastBlobErrorMessage(errorBody: unknown, fallbackMessage: string): Promise<void> {
    try {
      const result: any = await readBlobAsJson(errorBody as Blob);
      if (this.hasReadableErrorMessage(result)) {
        this.toastErrorMessage(result);
        return;
      }
    } catch {
      // 內容無法讀取時一律退回通用訊息 (unreadable body falls back to the generic message)
    }
    this.toastService.error(fallbackMessage);
  }

  /**
   * 判斷錯誤內容是否有可顯示的訊息。
   * 錯誤碼在該環境未建 Common.ErrorCode 資料列時後端回空 errorMsg，前端快取的覆寫訊息是另一個來源，兩者皆空才視為沒有訊息。
   *
   * The backend returns an empty errorMsg when the environment lacks the Common.ErrorCode row, and the front-end
   * cache is a second source, so a body counts as message-less only when both are empty.
   */
  private hasReadableErrorMessage(result: any): boolean {
    if (typeof result !== 'object' || result === null) {
      return false;
    }
    return !isNullOrEmpty(result.errorMsg)
      || !isNullOrEmpty(this.getMappingErrorMessage(result.category, result.errorCode));
  }

  /**
   * 取得錯誤訊息樣板。
   * Picks the error message template.
   *
   * 前端快取的原文依使用者於畫面選定的語系載入，優先採用以維持語系一致。例外是帶代入參數而快取原文用
   * 前端認不得的 %s 作佔位符：前端代入不了，改採伺服器已代入的訊息，否則畫面會顯示字面的 %s。
   */
  private resolveErrorMessageTemplate(err: any, hasDatas: boolean): string {
    const mappingMsg = this.getMappingErrorMessage(err.category, err.errorCode);
    if (isNullOrEmpty(mappingMsg)) {
      return err.errorMsg;
    }

    const cannotFillLocally = hasDatas && mappingMsg.includes('%s');
    if (cannotFillLocally && !isNullOrEmpty(err.errorMsg)) {
      return err.errorMsg;
    }
    return mappingMsg;
  }

  /**
   * Displays partial-success row errors from Entry A batch import.
   * Shape: { successCount, failCount, rowErrors: [{ rowIndex, rowIdentifier, errors }] }
   */
  toastBatchImportResult(result: any): void {
    if (!result) { return; }
    if (result.failCount > 0 && Array.isArray(result.rowErrors) && result.rowErrors.length > 0) {
      const lines: string[] = result.rowErrors.map((row: any) => {
        const id = row.rowIdentifier || `Row ${row.rowIndex + 1}`;
        const fieldMsgs: string = (row.errors || [])
          .map((e: any) => {
            const message = e.errorMsg || e.errorCode || '';
            return e.field && e.field !== '*' ? `[${e.field}] ${message}` : message;
          })
          .join(', ');
        return `${id}: ${fieldMsgs}`;
      });
      const summary = `${result.successCount} 筆成功，${result.failCount} 筆失敗:\n` + lines.join('\n');
      this.toastService.error(summary);
    }
  }

  getMappingErrorMessage(category: string, errorCode: string): string {
    const errorCodeGroup = this.cacheService.load(LaasConstants.errorCodeListKey);

    if (isNullOrEmpty(errorCodeGroup)) {
      return '';
    }

    const categoryGroup: any[] = isNullOrEmpty(errorCodeGroup[category]) ? [] : errorCodeGroup[category];
    const errMsg = categoryGroup.find(x => x.errorCode === errorCode)?.errorMsg ?? '';

    return errMsg;
  }
}
